/**
 * Holds the current Cloudflare Turnstile token for the auth forms.
 *
 * WHY A MODULE-LEVEL STORE RATHER THAN PROPS: the token is produced by a widget rendered
 * inside LoginForm/SignupMiniForm, but it has to reach Users.login()/Users.signup(). The
 * path between them runs through UseAuthFlowManager (~785 lines) and several callback
 * layers that have nothing to do with challenges. Threading a token through all of it
 * would touch far more code than the feature warrants, and every future auth form would
 * have to re-thread it.
 *
 * This is safe here specifically because the SPA mounts exactly ONE auth form at a time
 * (AuthFlowManager renders a single step). If that ever stops being true, the token must
 * become per-form state — a second concurrent widget would overwrite this one's token and
 * produce confusing `timeout-or-duplicate` rejections.
 *
 * TOKENS ARE SINGLE USE. Cloudflare redeems a token exactly once at siteverify. If the
 * server rejects and the user retries without the widget being reset, the second attempt
 * sends an already-redeemed token and is refused with `timeout-or-duplicate` — which
 * looks to the user like "it just failed twice". That is why consume() clears the value
 * and why the widget re-renders after every submit.
 */

let currentToken = '';
let resetWidget = null;

/** Called by the widget each time Cloudflare issues a token. */
export const setTurnstileToken = (token) => {
    currentToken = token || '';
};

/** Registers the widget's reset function so a failed submit can request a fresh token. */
export const registerTurnstileReset = (fn) => {
    resetWidget = typeof fn === 'function' ? fn : null;
};

/**
 * Returns the current token WITHOUT clearing it.
 * Used by the API layer when building a request body.
 */
export const getTurnstileToken = () => currentToken;

/**
 * Clears the stored token and asks the widget for a new one.
 *
 * Call after every submit that does not navigate away — a server-side rejection, an
 * inline validation error, anything that leaves the user on the form. Skipping this is
 * the single most common Turnstile integration bug.
 */
export const resetTurnstile = () => {
    currentToken = '';
    if (resetWidget) {
        resetWidget();
    }
};

/** True when a token is available. Lets a form disable submit until the challenge solves. */
export const hasTurnstileToken = () => currentToken !== '';

/** The public sitekey. Safe to ship in the bundle — it is designed to be public. */
export const TURNSTILE_SITEKEY = process.env.REACT_APP_TURNSTILE_SITEKEY || '';

/**
 * Whether the challenge is configured in this build.
 *
 * The BACKEND is the enforcement point and fails closed regardless of what the frontend
 * does, so this only controls whether a widget is rendered. It must never be used to
 * decide whether a request is allowed.
 */
export const isTurnstileEnabled = () => TURNSTILE_SITEKEY !== '';
